import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, BookOpen, ArrowRight } from "lucide-react";

/**
 * interface BookstoreTeaserProps {
 *   books: Array<{
 *     id: string;
 *     title: string;
 *     author: string;
 *     price: number;
 *     imageUrl?: string;
 *     shortDescription: string;
 *   }>;
 * }
 */
export default function BookstoreTeaser({ books = [] }) {
  // Use provided books or fallback to placeholder data if empty
  const displayBooks = books.length > 0 ? books : [
    {
      id: "book-1",
      title: "Firstfruits",
      author: "Chuck Pierce",
      price: 12.00,
      imageUrl: "https://assets.wordaliveglobal.org/products/48b1421a-e4d5-45e4-8ba2-6b106366bb70.jpg",
      shortDescription: "Understanding the power of giving the first to God."
    },
    {
      id: "book-2",
      title: "A Time to Advance",
      author: "Chuck Pierce",
      price: 12.00,
      imageUrl: "https://assets.wordaliveglobal.org/products/product-13-time-to-advance.jpg",
      shortDescription: "Understanding the significance of the Hebrew Tribes and Months."
    },
    {
      id: "book-3",
      title: "A Time to Prosper",
      author: "Chuck D. Pierce and Robert Heidler",
      price: 8.00,
      imageUrl: "https://assets.wordaliveglobal.org/products/product-02-time-to-prosper.jpg",
      shortDescription: "Finding and entering God's realm of blessings."
    },
    {
      id: "book-4",
      title: "The Joseph Mandate",
      author: "Michael Adeyemi Adefarasin",
      price: 19.00,
      imageUrl: "https://assets.wordaliveglobal.org/products/7dbcae60-3e94-4780-8c8c-7bc102329cac.jpg",
      shortDescription: "Calling a generation to steward the earth's resources."
    }
  ];

  return (
    <section data-testid="bookstore-teaser-section" className="py-20 md:py-32 bg-bone border-t border-bone-dark relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gold/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="container-page relative z-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag className="h-5 w-5 text-gold" />
              <span className="text-navy text-sm font-heading uppercase tracking-widest font-bold">
                Financial Sovereignty
              </span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-navy mb-4 leading-tight">
              Ministry Bookstore
            </h2>
            <p className="text-gray-600 text-base leading-relaxed max-w-2xl">
              Equip yourself with kingdom resources. Every purchase directly supports the Word Alive Global Center ministry without Big-Tech platform cuts.
            </p>
          </div>
          
          <Link to="/bookstore" className="hidden sm:block shrink-0">
            <Button className="bg-navy text-gold hover:bg-navy/90 shadow-[0_0_15px_rgba(212,175,55,0.2)] transition-all duration-300 rounded-full px-8 h-12 uppercase tracking-wider font-bold text-xs hover:-translate-y-0.5">
              Shop All Resources
            </Button>
          </Link>
        </div>

        {/* Desktop Grid / Mobile Horizontal Scroll */}
        <div className="flex overflow-x-auto md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 pb-8 pt-4 -mx-4 px-4 md:mx-0 md:px-0 md:pb-0 md:pt-0 snap-x snap-mandatory scrollbar-hide">
          {displayBooks.slice(0, 4).map((book) => (
            <Link 
              to="/bookstore" 
              key={book.id} 
              className="snap-center shrink-0 w-[70vw] max-w-[280px] md:w-auto block group h-full"
            >
              <Card className="h-full bg-white border border-bone-dark shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 rounded-2xl overflow-hidden flex flex-col relative">
                {/* Image Section */}
                <div className="aspect-[4/5] bg-bone-dark relative overflow-hidden flex items-center justify-center p-6">
                  {book.image_url || book.imageUrl ? (
                    <img 
                      src={book.image_url || book.imageUrl} 
                      alt={book.name || book.title} 
                      className="w-auto h-full object-contain drop-shadow-xl group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-24 h-32 bg-bone rounded shadow-inner flex items-center justify-center border border-navy/10 group-hover:scale-105 transition-transform duration-500">
                      <BookOpen className="h-8 w-8 text-navy/20" />
                    </div>
                  )}
                  
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/20 to-transparent pointer-events-none" />
                </div>
                
                {/* Content Section */}
                <CardContent className="p-6 flex-1 flex flex-col bg-white">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-heading text-xl text-navy leading-snug group-hover:text-gold transition-colors duration-300 line-clamp-2">
                      {book.name || book.title}
                    </h3>
                  </div>
                  
                  {book.author && (
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
                      {book.author}
                    </p>
                  )}
                  
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-6 flex-1">
                    {book.description || book.shortDescription || "Ministry resource available in our bookstore."}
                  </p>
                  
                  <div className="mt-auto flex items-center justify-between">
                    <span className="font-heading text-lg font-bold text-gold">
                      ${typeof book.price === 'number' ? book.price.toFixed(2) : parseFloat(book.price || 0).toFixed(2)}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                      <ShoppingBag className="h-4 w-4 text-gold group-hover:text-navy" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
          
          {/* Mobile "View All" Card */}
          {books.length > 4 && (
            <Link to="/bookstore" className="md:hidden block snap-center shrink-0 w-[40vw] max-w-[160px] h-full">
              <Card className="h-full bg-navy border-none shadow-md rounded-2xl flex items-center justify-center p-6 text-center group min-h-[300px]">
                <div>
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4 group-active:scale-95 transition-transform">
                    <ArrowRight className="h-6 w-6 text-gold" />
                  </div>
                  <p className="text-bone font-heading text-lg">Shop All Resources</p>
                </div>
              </Card>
            </Link>
          )}
        </div>

        {/* Mobile CTA */}
        <div className="mt-6 md:hidden text-center">
          <Link to="/bookstore">
            <Button className="w-full bg-navy text-gold hover:bg-navy/90 shadow-md rounded-full h-12 font-bold uppercase tracking-wider text-xs">
              Shop All Resources
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}